/* eslint-disable fp/no-mutation */

import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { useForm } from '@tanstack/react-form'
import { z } from 'zod'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  Check as SubmitIcon,
  Cancel as CancelIcon,
  CloudUpload as UploadIcon,
  InsertDriveFile as UploadFileIcon,
  Close as ClearIcon,
} from '@mui/icons-material'
import axios from 'axios'
import {
  getZodValidationError,
  shouldShowZodFieldError,
} from 'src/v3/utilities/validation'
import { EXTERNAL_CERT_HOUR_TYPES } from '../../../constants'
import { CredentialFileData } from '../../types'
import {
  addExternalCertificate,
  updateExternalCertificate,
  updateExternalCertificateImage,
} from '../../../utils/api'

dayjs.extend(utc)

interface ExternalCertificateModalProps {
  isOpen: boolean
  certificateID?: string
  userID: string
  linkedServiceID: string
  onClose: () => void
  onSuccess: () => void
  onFailure: (error: string) => void
}

export default function ExternalCertificateModal({
  isOpen,
  certificateID,
  userID,
  linkedServiceID,
  onClose,
  onSuccess,
  onFailure,
}: ExternalCertificateModalProps) {
  let [fileToUpload, setFileToUpload] = useState<
    CredentialFileData | undefined
  >()
  let [fileError, setFileError] = useState<string>('')

  let validationSchema = z.object({
    name: z.string().nonempty('Name is required'),
    courseCode: z.string().nonempty('Course Code is required'),
    length: z
      .number('Length must be a number')
      .min(0.5, 'Length must be at least 0.5'),
    hourType: z.string().refine((val) => val !== 'unselected', {
      message: 'Hour Type is required',
    }),
    courseType: z.enum(['recorded', 'live']),
    dateCompleted: z
      .string()
      .nonempty('Date Completed is required')
      .refine((val) => dayjs(val, 'MM/DD/YYYY', true).isValid(), {
        message: 'Date Completed is invalid',
      }),
  })

  let form = useForm({
    defaultValues: {
      name: '',
      courseCode: '',
      length: 0,
      hourType: 'unselected',
      courseType: 'recorded',
      dateCompleted: '',
    },
    validators: {
      onMount: validationSchema,
      onChange: validationSchema,
    },
    onSubmit: async ({ value }) => {
      if (!fileToUpload) {
        setFileError('File is required')
        return
      }

      if (!certificateID) {
        addExternalCertificate({ userID, linkedServiceID, ...value })
          .then((response: any) =>
            updateExternalCertificateImage({
              id: response.id,
              imageType:
                fileToUpload.extension === 'jpeg'
                  ? 'jpg'
                  : fileToUpload.extension,
            }),
          )
          .then((presignedUrl) =>
            axios.put(presignedUrl, fileToUpload.file, {
              headers: {
                'Cache-Control': 'max-age=2592000',
                'content-type': fileToUpload.type,
              },
            }),
          )
          .then(() => {
            // eslint-disable-next-line no-use-before-define
            handleClose()
            onSuccess()
          })
          .catch((error) => {
            let errorMessage = `Error adding the external certificate ${value.name}.`
            // eslint-disable-next-line no-console
            console.error(errorMessage, error)
            onFailure(errorMessage)
          })
      } else {
        updateExternalCertificate({
          id: certificateID,
          userID,
          linkedServiceID,
          ...value,
        })
          // @ts-expect-error - Promise type mismatch with conditional return
          .then(() => {
            if (fileToUpload) {
              return updateExternalCertificateImage({
                id: certificateID,
                imageType:
                  fileToUpload.extension === 'jpeg'
                    ? 'jpg'
                    : fileToUpload.extension,
              }).then((presignedUrl) =>
                axios.put(presignedUrl, fileToUpload.file, {
                  headers: {
                    'Cache-Control': 'max-age=2592000',
                    'content-type': fileToUpload.type,
                  },
                }),
              )
            }
            return Promise.resolve()
          })
          .then(() => {
            // eslint-disable-next-line no-use-before-define
            handleClose()
            onSuccess()
          })
          .catch((error) => {
            let errorMessage = `Error updating the external certificate ${value.name}.`
            // eslint-disable-next-line no-console
            console.error(errorMessage, error)
            onFailure(errorMessage)
          })
      }
    },
  })

  let handleCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    let file = e.target.files?.[0]
    if (!file) return

    let fileName = file.name
    let reader = new FileReader()
    reader.onloadend = () => {
      let blobData = new Blob([reader.result as ArrayBuffer], {
        type: file.type,
      })
      let fileExtension =
        file.type === 'application/pdf'
          ? 'pdf'
          : file.type === 'image/jpeg'
            ? 'jpg'
            : 'png'

      setFileToUpload({
        name: fileName,
        type: file.type,
        extension: fileExtension,
        file: blobData,
      })
      setFileError('')
    }
    reader.readAsArrayBuffer(file)
  }

  // Handle modal close
  let handleClose = () => {
    form.reset()
    setFileToUpload(undefined)
    setFileError('')
    onClose()
  }

  useEffect(() => {
    if (isOpen) {
      form.update()
      form.validate('mount')
    }
  }, [isOpen])

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      aria-labelledby="add-external-certificate-dialog-title"
      aria-describedby="add-external-certificate-description"
      maxWidth="xs"
    >
      <Paper className="px-4 py-2">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
          aria-label="Add external certificate form"
        >
          <DialogTitle
            id="add-external-certificate-dialog-title"
            className="text-center"
          >
            Add External Certificate
          </DialogTitle>
          <DialogContent className="flex flex-col gap-4 px-2 pb-6">
            <Typography
              id="add-external-certificate-description"
              variant="body2"
              sx={{ display: 'none' }}
            >
              Form to add a new external certificate. All fields are required.
            </Typography>

            <form.Field name="name">
              {(field) => (
                <TextField
                  className="mt-2"
                  variant="outlined"
                  size="small"
                  label="Name"
                  type="text"
                  fullWidth
                  required
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  error={shouldShowZodFieldError(field.state.meta)}
                  helperText={
                    shouldShowZodFieldError(field.state.meta) &&
                    getZodValidationError(field.state.meta.errors)
                  }
                />
              )}
            </form.Field>

            <form.Field name="length">
              {(field) => (
                <TextField
                  variant="outlined"
                  size="small"
                  label="Length"
                  type="number"
                  fullWidth
                  required
                  value={field.state.value || ''}
                  onChange={(e) =>
                    field.handleChange(
                      e.target.value ? Number(e.target.value) : 0,
                    )
                  }
                  error={shouldShowZodFieldError(field.state.meta)}
                  helperText={
                    shouldShowZodFieldError(field.state.meta) &&
                    getZodValidationError(field.state.meta.errors)
                  }
                />
              )}
            </form.Field>

            <form.Field name="courseCode">
              {(field) => (
                <TextField
                  variant="outlined"
                  size="small"
                  label="Course Code"
                  type="text"
                  fullWidth
                  required
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  error={shouldShowZodFieldError(field.state.meta)}
                  helperText={
                    shouldShowZodFieldError(field.state.meta) &&
                    getZodValidationError(field.state.meta.errors)
                  }
                />
              )}
            </form.Field>

            <form.Field name="hourType">
              {(field) => (
                <FormControl
                  fullWidth
                  required
                  error={shouldShowZodFieldError(field.state.meta)}
                >
                  <InputLabel size="small">Credential Type</InputLabel>
                  <Select
                    value={field.state.value}
                    label="Hour Type"
                    onChange={(e) => field.handleChange(e.target.value)}
                    variant="outlined"
                    size="small"
                  >
                    <MenuItem value="unselected">Unselected</MenuItem>
                    {EXTERNAL_CERT_HOUR_TYPES.map((t) => (
                      <MenuItem
                        key={t}
                        value={t.replace(/ /g, '').toLowerCase()}
                      >
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                  {shouldShowZodFieldError(field.state.meta) && (
                    <Typography
                      variant="caption"
                      color="error"
                      className="mt-1 ml-3.5"
                    >
                      {getZodValidationError(field.state.meta.errors)}
                    </Typography>
                  )}
                </FormControl>
              )}
            </form.Field>

            <form.Field name="courseType">
              {(field) => (
                <FormControl fullWidth>
                  <FormLabel sx={{ mb: 1 }}>Course Type</FormLabel>
                  <RadioGroup
                    row
                    name="courseType"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  >
                    <FormControlLabel
                      value="recorded"
                      control={<Radio />}
                      label="Recorded"
                    />
                    <FormControlLabel
                      value="live"
                      control={<Radio />}
                      label="Live"
                    />
                  </RadioGroup>
                </FormControl>
              )}
            </form.Field>

            <form.Field name="dateCompleted">
              {(field) => (
                <Box>
                  <Box className="flex justify-center">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Date Completed"
                        value={
                          field.state.value
                            ? dayjs.utc(field.state.value)
                            : null
                        }
                        onChange={(val) =>
                          field.handleChange(
                            val ? dayjs.utc(val).format('MM/DD/YYYY') : '',
                          )
                        }
                        slotProps={{
                          textField: {
                            required: true,
                            error: shouldShowZodFieldError(field.state.meta),
                            helperText:
                              shouldShowZodFieldError(field.state.meta) &&
                              getZodValidationError(field.state.meta.errors),
                            variant: 'outlined',
                            size: 'small',
                            fullWidth: true,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Box>
              )}
            </form.Field>

            <Box>
              <label htmlFor="add-external-certificate-file-upload">
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  fullWidth
                  color={fileError ? 'error' : 'primary'}
                  aria-describedby={
                    fileError
                      ? 'file-upload-error file-upload-helper'
                      : 'file-upload-helper'
                  }
                >
                  <input
                    id="add-external-certificate-file-upload"
                    accept=".png,.jpg,.jpeg,.pdf"
                    style={{ display: 'none' }}
                    type="file"
                    onChange={handleCapture}
                    aria-label="Select certificate file (required)"
                    aria-required="true"
                    aria-invalid={fileError ? 'true' : 'false'}
                    aria-describedby={
                      fileError
                        ? 'file-upload-error file-upload-helper'
                        : 'file-upload-helper'
                    }
                  />
                  Select File *
                </Button>
              </label>
              {fileError && (
                <Typography
                  id="file-upload-error"
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}
                  role="alert"
                >
                  {fileError}
                </Typography>
              )}
              {fileToUpload && (
                <Box className="flex items-center justify-center gap-2 mt-2">
                  <Typography variant="subtitle1" className="flex items-center">
                    <UploadFileIcon
                      color="primary"
                      sx={{ mr: '2px', mb: '2px' }}
                    />
                    {fileToUpload.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setFileToUpload(undefined)}
                    aria-label="Clear file selection"
                    sx={{ color: 'text.secondary' }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
            <Typography
              id="file-upload-helper"
              variant="caption"
              color="textSecondary"
            >
              * Allowed file types: png, jpg, pdf
              <br />* File upload is required when creating a new credential.
            </Typography>
          </DialogContent>

          <DialogActions
            disableSpacing={true}
            className="flex-col gap-2 pt-0 pb-6 mt-0"
          >
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isSubmitting]) => (
                <>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    fullWidth
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={20} />
                      ) : (
                        <SubmitIcon />
                      )
                    }
                    disabled={!canSubmit || isSubmitting}
                  >
                    Submit
                  </Button>
                  <Button
                    onClick={handleClose}
                    color="error"
                    variant="contained"
                    fullWidth
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                </>
              )}
            />
          </DialogActions>
        </form>
      </Paper>
    </Dialog>
  )
}
